/**
 * 客戶資料 Excel 匯出（需求 R22）
 *
 * 版面依附件「客戶管理資料-系統用.xlsx」：
 *   第 1 列：公司資訊 + 「客戶資料表」標題
 *   第 3 列：欄位表頭（凍結窗格）
 *   第 4 列起：資料列
 *
 * 欄位順序（PM 2026-05-18 補充：完全不匯出聯絡人2；需求 R27 加入付款方式）：
 *   A 客戶編號 / B 客戶級別 / C 客戶國別 / D 客戶名稱 / E 客戶簡稱 / F 客戶地址
 *   G–J 聯絡人1（姓名/職稱/Email/電話）
 *   K 客戶來源 / L 備註 / M 付款方式 / N 負責業務
 *
 * 與 PM（2026-05-18）確認結論：
 *   1. 聯絡人欄位僅輸出聯絡人1，聯絡人2 之四欄完全刪除（不是留空）。
 *   2. 篩選條件變更 → 前端自動清除全部已選（此檔不負責；UI 層處理）。
 *   3. 匯出權限：主管（manager）— 路由層已限制。
 *
 * 與報價單匯出共用 ExcelJS 4.4。
 */

const FONT = { name: 'Microsoft JhengHei' };

const C = {
  titleBg:   'FFFFECB3',
  headerBg:  'FFFFD54F',
  headerTxt: 'FF5D4037',
  border:    'FFE6C9A3',
  white:     'FFFFFFFF',
  dark:      'FF333333',
};

const solid = (argb) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });
const bs = (argb, style = 'thin') => ({ style, color: { argb } });
const thinBorder = {
  top: bs(C.border), left: bs(C.border), right: bs(C.border), bottom: bs(C.border),
};

// 表頭定義（中文標籤 + 欄寬）— 不含聯絡人2 欄位（PM 2026-05-18 確認補充）
const COLUMNS = [
  { key: 'customerNumber', label: '客戶編號', width: 14 },
  { key: 'level',          label: '客戶級別', width: 10 },
  { key: 'country',        label: '客戶國別', width: 12 },
  { key: 'name',           label: '客戶名稱', width: 28 },
  { key: 'shortName',      label: '客戶簡稱', width: 16 },
  { key: 'address',        label: '客戶地址', width: 36 },
  { key: 'contact1Name',   label: '聯絡人 姓名',  width: 14 },
  { key: 'contact1Title',  label: '聯絡人 職稱',  width: 14 },
  { key: 'contact1Email',  label: '聯絡人 Email', width: 22 },
  { key: 'contact1Phone',  label: '聯絡人 電話',  width: 16 },
  { key: 'source',         label: '客戶來源', width: 14 },
  { key: 'notes',          label: '備註',     width: 30 },
  { key: 'paymentMethod',  label: '付款方式', width: 16 },
  { key: 'assignedSales',  label: '負責業務', width: 14 },
];

const TOTAL_COLS = COLUMNS.length; // 14 欄（A..N，需求 R27 加入付款方式後）

function pickContact(contacts, idx) {
  const c = (contacts || [])[idx];
  if (!c) return { name: '', title: '', email: '', phone: '' };
  return {
    name: c.name || '',
    title: c.title || '',
    email: c.email || '',
    phone: c.phone || '',
  };
}

function rowFromCustomer(cust) {
  const c1 = pickContact(cust.contacts, 0);
  // assignedSalesId 來自後端 populate 後可能是 { _id, name } 或 ObjectId 字串；未指派則為 null。
  const sales = cust.assignedSalesId && typeof cust.assignedSalesId === 'object'
    ? (cust.assignedSalesId.name || '')
    : '';
  return {
    customerNumber: cust.customerNumber || '',
    level: cust.level ? `${cust.level}級` : '',
    country: cust.country || '',
    name: cust.name || '',
    shortName: cust.shortName || '',
    address: cust.address || '',
    contact1Name: c1.name,
    contact1Title: c1.title,
    contact1Email: c1.email,
    contact1Phone: c1.phone,
    source: cust.source || '',
    notes: cust.notes || '',
    paymentMethod: cust.paymentMethod || '',
    assignedSales: sales,
  };
}

function tsForFilename(d = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
}

async function fetchCompanyName() {
  try {
    const token = localStorage.getItem('token');
    const res = await fetch('/api/companies', {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) return '';
    const companies = await res.json();
    return (Array.isArray(companies) && companies[0]?.name) || '';
  } catch {
    return '';
  }
}

export async function exportCustomersExcel({ customers }) {
  const { Workbook } = await import('exceljs');
  const companyName = await fetchCompanyName();

  const workbook = new Workbook();
  const sheet = workbook.addWorksheet('客戶資料', {
    views: [{ state: 'frozen', xSplit: 0, ySplit: 3 }], // 凍結至第 3 列（含表頭）
    pageSetup: {
      paperSize: 9, orientation: 'landscape', fitToPage: true,
      fitToWidth: 1, fitToHeight: 0,
      margins: { left: 0.3, right: 0.3, top: 0.3, bottom: 0.3, header: 0, footer: 0 },
    },
  });

  sheet.columns = COLUMNS.map((c) => ({ width: c.width }));

  // ── Row 1：標題列（公司資訊 + 「客戶資料表」） ──
  sheet.mergeCells(1, 1, 1, TOTAL_COLS);
  const title = sheet.getCell(1, 1);
  title.value = `${companyName ? companyName + '　' : ''}客戶資料表`;
  title.font = { ...FONT, size: 16, bold: true, color: { argb: C.headerTxt } };
  title.alignment = { vertical: 'middle', horizontal: 'center' };
  title.fill = solid(C.titleBg);
  sheet.getRow(1).height = 30;

  // ── Row 2：空白 spacer（保留模板版面）──
  sheet.getRow(2).height = 8;

  // ── Row 3：欄位表頭 ──
  const headerRow = sheet.getRow(3);
  headerRow.height = 28;
  COLUMNS.forEach((col, i) => {
    const cell = sheet.getCell(3, i + 1);
    cell.value = col.label;
    cell.font = { ...FONT, size: 11, bold: true, color: { argb: C.headerTxt } };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.fill = solid(C.headerBg);
    cell.border = thinBorder;
  });

  // ── Row 4+：資料列 ──
  customers.forEach((cust, idx) => {
    const r = 4 + idx;
    const data = rowFromCustomer(cust);
    COLUMNS.forEach((col, i) => {
      const cell = sheet.getCell(r, i + 1);
      cell.value = data[col.key];
      cell.font = { ...FONT, size: 10, color: { argb: C.dark } };
      cell.alignment = {
        vertical: 'middle',
        horizontal: col.key === 'notes' || col.key === 'address' ? 'left' : 'center',
        wrapText: true,
      };
      cell.fill = solid(C.white);
      cell.border = thinBorder;
    });
    sheet.getRow(r).height = 22;
  });

  // 下載
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `客戶資料_${tsForFilename()}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
